/**
 * Banner Management Panel
 * Admin panel for managing site banners
 */
import { useState, useEffect, useRef } from 'react';
import { apiClient, Banner } from '../lib/api-client';
import { resolveMediaUrl } from '../lib/media-url';

// Simple inline SVG icons
const PhotoIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const PencilIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
);

const XMarkIcon = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

// Banner location labels
const LOCATION_LABELS: Record<string, string> = {
    streaming_list: '配信一覧ページ',
    room_header: 'ルームヘッダー',
};

// Recommended image sizes for each banner location
const RECOMMENDED_SIZES: Record<string, { width: number; height: number; description: string }> = {
    streaming_list: { width: 728, height: 90, description: '横長バナー（728×90px）' },
    room_header: { width: 468, height: 60, description: 'ヘッダーバナー（468×60px）' },
};

// Preview state type
interface PreviewState {
    location: string;
    file: File;
    previewUrl: string;
    imageWidth: number;
    imageHeight: number;
    aspectRatioWarning: string | null;
}

export default function BannerManagementPanel() {
    const [banners, setBanners] = useState<Banner[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [uploadingLocation, setUploadingLocation] = useState<string | null>(null);
    const [editingBanner, setEditingBanner] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{ alt_text: string; link_url: string }>({ alt_text: '', link_url: '' });
    const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const [preview, setPreview] = useState<PreviewState | null>(null);

    useEffect(() => {
        loadBanners();
    }, []);

    const loadBanners = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.getAllBanners();
            setBanners(response.banners);
        } catch (err) {
            console.error('Failed to load banners:', err);
            setError('バナーの読み込みに失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (location: string, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('JPEG, PNG, GIF, WebP形式の画像のみアップロードできます');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('ファイルサイズは5MB以下にしてください');
            return;
        }

        // Create preview URL and check dimensions
        const previewUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const recommended = RECOMMENDED_SIZES[location];
            let warning: string | null = null;

            if (recommended) {
                const recommendedRatio = recommended.width / recommended.height;
                const actualRatio = img.width / img.height;
                const ratioDiff = Math.abs(recommendedRatio - actualRatio) / recommendedRatio;

                // Warn if aspect ratio differs by more than 15%
                if (ratioDiff > 0.15) {
                    warning = `選択した画像の比率（${img.width}×${img.height}px）は推奨サイズ（${recommended.width}×${recommended.height}px）と異なります。表示時に一部がトリミングされる可能性があります。`;
                }
            }

            setPreview({
                location,
                file,
                previewUrl,
                imageWidth: img.width,
                imageHeight: img.height,
                aspectRatioWarning: warning,
            });
        };
        img.src = previewUrl;

        // Reset file input
        event.target.value = '';
    };

    const handleUploadConfirm = async () => {
        if (!preview) return;

        try {
            setUploadingLocation(preview.location);
            setError(null);
            const result = await apiClient.uploadBannerImage(preview.location, preview.file);

            // Update banner in list
            setBanners(prev => prev.map(b =>
                b.location === preview.location ? { ...b, image_path: result.image_path } : b
            ));

            // Clean up preview
            URL.revokeObjectURL(preview.previewUrl);
            setPreview(null);
        } catch (err) {
            console.error('Failed to upload banner:', err);
            setError('画像のアップロードに失敗しました');
        } finally {
            setUploadingLocation(null);
        }
    };

    const handlePreviewCancel = () => {
        if (preview) {
            URL.revokeObjectURL(preview.previewUrl);
            setPreview(null);
        }
    };

    const handleEditClick = (banner: Banner) => {
        setEditingBanner(banner.location);
        setEditForm({
            alt_text: banner.alt_text || '',
            link_url: banner.link_url || '',
        });
    };

    const handleEditSave = async (location: string) => {
        try {
            setError(null);
            await apiClient.updateBanner(location, {
                alt_text: editForm.alt_text,
                link_url: editForm.link_url,  // 空文字列でも送信（バックエンドでNULLに変換）
            });

            // Update banner in list
            setBanners(prev => prev.map(b =>
                b.location === location
                    ? { ...b, alt_text: editForm.alt_text, link_url: editForm.link_url || null }
                    : b
            ));
            setEditingBanner(null);
        } catch (err) {
            console.error('Failed to update banner:', err);
            setError('バナーの更新に失敗しました');
        }
    };

    const triggerFileSelect = (location: string) => {
        fileInputRefs.current[location]?.click();
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-32 bg-gray-200 rounded"></div>
                    <div className="h-32 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">バナー管理</h2>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            <div className="space-y-6">
                {banners.map((banner) => (
                    <div
                        key={banner.location}
                        className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                    >
                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <div>
                                <h3 className="font-medium text-gray-800">
                                    {LOCATION_LABELS[banner.location] || banner.location}
                                </h3>
                                <p className="text-sm text-gray-500">
                                    場所: {banner.location}
                                </p>
                            </div>
                        </div>

                        <div className="p-4">
                            {/* Image preview and upload */}
                            <div className="flex gap-4 mb-4">
                                <div className="flex-1">
                                    {banner.image_path ? (
                                        <img
                                            src={resolveMediaUrl(banner.image_path)}
                                            alt={banner.alt_text || ''}
                                            className="w-full max-h-48 object-contain bg-gray-100 rounded"
                                        />
                                    ) : (
                                        <div className="w-full h-32 bg-gray-100 rounded flex items-center justify-center">
                                            <PhotoIcon className="w-12 h-12 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col justify-center">
                                    <button
                                        onClick={() => triggerFileSelect(banner.location)}
                                        disabled={uploadingLocation === banner.location}
                                        className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg disabled:opacity-50"
                                    >
                                        {uploadingLocation === banner.location ? 'アップロード中...' : '画像を変更'}
                                    </button>
                                    <input
                                        ref={(el) => { fileInputRefs.current[banner.location] = el; }}
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif,image/webp"
                                        onChange={(e) => handleFileSelect(banner.location, e)}
                                        className="hidden"
                                    />
                                    <p className="text-xs text-gray-500 mt-2">
                                        JPEG, PNG, GIF, WebP<br />
                                        最大5MB
                                    </p>
                                    {RECOMMENDED_SIZES[banner.location] && (
                                        <p className="text-xs text-pink-600 mt-1 font-medium">
                                            推奨: {RECOMMENDED_SIZES[banner.location].description}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Edit form */}
                            {editingBanner === banner.location ? (
                                <div className="space-y-3 border-t border-gray-200 pt-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            代替テキスト (Alt)
                                        </label>
                                        <input
                                            type="text"
                                            value={editForm.alt_text}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, alt_text: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                                            placeholder="バナーの説明"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            リンクURL (任意)
                                        </label>
                                        <input
                                            type="url"
                                            value={editForm.link_url}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, link_url: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEditSave(banner.location)}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded"
                                        >
                                            <CheckIcon className="w-4 h-4" />
                                            保存
                                        </button>
                                        <button
                                            onClick={() => setEditingBanner(null)}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded"
                                        >
                                            <XMarkIcon className="w-4 h-4" />
                                            キャンセル
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="border-t border-gray-200 pt-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <div className="text-gray-600">
                                            <span className="font-medium">Alt:</span> {banner.alt_text || '(未設定)'}
                                            {banner.link_url && (
                                                <span className="ml-4">
                                                    <span className="font-medium">リンク:</span>{' '}
                                                    <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:underline">
                                                        {banner.link_url.length > 40 ? banner.link_url.substring(0, 40) + '...' : banner.link_url}
                                                    </a>
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleEditClick(banner)}
                                            className="flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                                        >
                                            <PencilIcon className="w-4 h-4" />
                                            編集
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {banners.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        バナーが登録されていません
                    </div>
                )}
            </div>

            {/* Preview Modal */}
            {preview && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg max-w-2xl w-full mx-4 overflow-hidden">
                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="font-medium text-gray-800">
                                アップロード確認 - {LOCATION_LABELS[preview.location] || preview.location}
                            </h3>
                            <button
                                onClick={handlePreviewCancel}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4">
                            {/* Preview image */}
                            <div className="mb-4">
                                <p className="text-sm text-gray-600 mb-2">プレビュー:</p>
                                <img
                                    src={preview.previewUrl}
                                    alt="プレビュー"
                                    className="w-full max-h-64 object-contain bg-gray-100 rounded"
                                />
                            </div>

                            {/* Image info */}
                            <div className="mb-4 text-sm text-gray-600">
                                <p>画像サイズ: {preview.imageWidth} × {preview.imageHeight} px</p>
                                {RECOMMENDED_SIZES[preview.location] && (
                                    <p>推奨サイズ: {RECOMMENDED_SIZES[preview.location].description}</p>
                                )}
                            </div>

                            {/* Aspect ratio warning */}
                            {preview.aspectRatioWarning && (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-sm text-yellow-800 flex items-start gap-2">
                                        <span className="text-yellow-600 font-bold">⚠</span>
                                        {preview.aspectRatioWarning}
                                    </p>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={handlePreviewCancel}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                                >
                                    キャンセル
                                </button>
                                <button
                                    onClick={handleUploadConfirm}
                                    disabled={uploadingLocation === preview.location}
                                    className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded disabled:opacity-50"
                                >
                                    {uploadingLocation === preview.location ? 'アップロード中...' : 'アップロード'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
